import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FilterListIcon from '@mui/icons-material/FilterList';
import Chart from 'react-apexcharts';

// ── Mock Data ──────────────────────────────────────────────────────────
const MOCK_MONTHLY_TREND = {
  options: {
    chart: { type: 'area', toolbar: { show: false } },
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 90, 100] }
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: [
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
        'Jan',
        'Feb',
        'Mar'
      ],
      labels: { style: { colors: '#718096' } }
    },
    yaxis: { labels: { style: { colors: '#718096' } } },
    colors: ['#4fbbc8', '#e05252'],
    legend: { position: 'top', horizontalAlign: 'right' }
  },
  series: [
    { name: 'Income (Input)', data: [45, 52, 38, 65, 48, 72, 58, 62, 85, 92, 88, 105] },
    { name: 'Expense (Output)', data: [32, 40, 45, 42, 38, 55, 50, 48, 60, 65, 70, 75] }
  ]
};

const MOCK_EXPENSE_PIE = {
  series: [45, 25, 15, 10, 5],
  options: {
    labels: ['Staff Salaries', 'Office Rent', 'Marketing', 'Utilities', 'Others'],
    colors: ['#3b7dd8', '#4fbbc8', '#e08c2a', '#e05252', '#718096'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
    plotOptions: { pie: { donut: { size: '65%' } } }
  }
};

const MOCK_KPI = [
  {
    id: 'revenue',
    label: 'Total Revenue (Input)',
    value: '₹ 8.45 Cr',
    icon: <TrendingUpIcon sx={{ fontSize: '2rem', color: '#4fbbc8' }} />,
    colorClass: 'acct-kpi-card acct-kpi-card--teal',
    note: 'Total Inflow'
  },
  {
    id: 'expense',
    label: 'Total Expense (Output)',
    value: '₹ 4.12 Cr',
    icon: <TrendingDownIcon sx={{ fontSize: '2rem', color: '#e05252' }} />,
    colorClass: 'acct-kpi-card acct-kpi-card--red',
    note: 'Total Outflow'
  },
  {
    id: 'netProfit',
    label: 'Net Balance',
    value: '₹ 4.33 Cr',
    icon: <AccountBalanceWalletIcon sx={{ fontSize: '2rem', color: '#3b7dd8' }} />,
    colorClass: 'acct-kpi-card acct-kpi-card--blue',
    note: 'Input - Output'
  },
  {
    id: 'pendingDues',
    label: 'Pending Receivables',
    value: '₹ 1.25 Cr',
    icon: <PendingActionsIcon sx={{ fontSize: '2rem', color: '#e08c2a' }} />,
    colorClass: 'acct-kpi-card acct-kpi-card--amber',
    note: 'Unrealized Input'
  }
];

export default function AccountingDashboard() {
  return (
    <Box>
      {/* ── KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {MOCK_KPI.map((kpi) => (
          <Grid key={kpi.id} item xs={12} sm={6} md={3}>
            <Card className={kpi.colorClass} sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#718096', fontWeight: 500, letterSpacing: 0.5 }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#2d3748', mt: 0.5, mb: 0.25 }}
                  >
                    {kpi.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                    {kpi.note}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.55)'
                  }}
                >
                  {kpi.icon}
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Visual Analysis (Input vs Output) ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} color="#2d3748" mb={2}>
              Monthly Input (Income) vs Output (Expense) Trend
            </Typography>
            <Chart
              options={MOCK_MONTHLY_TREND.options}
              series={MOCK_MONTHLY_TREND.series}
              type="area"
              height={300}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} color="#2d3748" mb={2}>
              Output Analysis (Expense Breakup)
            </Typography>
            <Chart
              options={MOCK_EXPENSE_PIE.options}
              series={MOCK_EXPENSE_PIE.series}
              type="donut"
              height={335}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
